import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Home, Clock, Target, Trophy, BarChart3, BookOpen } from "lucide-react";
import { ThemeContext } from "../ThemeContext";

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const MultiPlayerPage = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const navigate = useNavigate();
    const handleEnterLobby = () => {
        navigate('/lobby-multiplayer');
    };

    return (
        <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
            {/* Main Content */}
            <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
                {/* Animated Header */}
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, type: "spring" }}
                    className="text-center mb-8"
                >
                    <h1
                        key={`title-${isDarkMode}`}
                        className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
                        style={{
                            color: isDarkMode ? '#ff7a00' : '#212D82',
                            background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
                            backgroundSize: '200% 100%',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'shine 10s linear infinite',
                            lineHeight: '1.2',
                            paddingBottom: '0.25rem'
                        }}
                    >
                        Welcome to Compete Mode!
                    </h1>
                    <motion.p
                        className="text-gray-600 dark:text-gray-400 text-xl font-zonaProRegular"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        Compete against other players in real-time! Speed and accuracy are key - the first correct answer wins!
                    </motion.p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 max-w-7xl mx-auto">
                    {/* Left Column: Welcome Message */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="flex flex-col items-center justify-center lg:col-span-2"
                    >
                        <Card delay={0.3} className="w-full">
                            <div className="text-center flex flex-col items-center">
                                <motion.div
                                    animate={{ rotate: [0, 10, -10, 0] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="mb-6"
                                >
                                    <Users className="w-20 h-20 text-cyan-500" />
                                </motion.div>
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 mb-4">
                                    Ready to Compete?
                                </h2>
                                <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-xl font-zonaProRegular">
                                    Join or create rooms and answer questions faster than your opponents to win points. Let the competition begin!
                                </p>
                                <motion.button
                                    onClick={handleEnterLobby}
                                    className="py-3 px-8 bg-[#34c759] text-white text-xl sm:text-2xl md:text-2xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Play Now
                                </motion.button>
                            </div>
                        </Card>
                    </motion.div>

                    {/* Right Column: Game Rules */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="flex flex-col justify-center lg:col-span-3"
                    >
                        <Card delay={0.5} className="w-full">
                            <div className="flex items-center gap-3 mb-6">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                    className="p-2 rounded-xl bg-[#a6a6a6] text-white"
                                >
                                    <BookOpen size={24} />
                                </motion.div>
                                <h2
                                    key={`howtoplay-${isDarkMode}`}
                                    className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra relative inline-block"
                                    style={{
                                        color: isDarkMode ? '#ff7a00' : '#212D82',
                                        background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
                                        backgroundSize: '200% 100%',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        animation: 'shine 10s linear infinite',
                                        lineHeight: '1.2',
                                        paddingBottom: '0.25rem'
                                    }}
                                >
                                    How to Play
                                </h2>
                            </div>
                            <ul className="space-y-6 text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 font-zonaProRegular">
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🏠
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Lobby & Rooms:</strong> Enter the lobby to join or create rooms. Choose a subject (Math, English, etc.), select a difficulty level (Easy, Medium, Hard, or Random), and pick a room type (Public or Private with password). Rooms support up to 8 players, and questions continue indefinitely until the last player leaves.
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.7 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        ⏳
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Waiting Room:</strong> Once in a room, wait for other players to join. The host can start the game when ready. Private rooms require password verification. Players joining mid-game will wait for the current round to finish before joining.
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🎯
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Difficulty & Points:</strong> Questions are worth points based on their difficulty level. Points are displayed on screen for each question:
                                        <ul className="ml-6 mt-2 space-y-2 list-disc list-inside">
                                            <li><strong className="text-gray-800 dark:text-gray-200">Easy:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+100 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-100 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-gray-200">Medium:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+200 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-200 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-gray-200">Hard:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+300 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-300 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-gray-200">Random:</strong> Difficulty changes per question and is shown on screen</li>
                                        </ul>
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.9 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        ⚡
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Scoring & Winning:</strong> Answer questions within 90 seconds. The first player to answer correctly wins the points. If the first answer is wrong, that player loses points and the next correct answer wins. All other players get 0 points.
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.0 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 2 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🏆
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Leaderboard:</strong> Points are updated in real-time. Be fast and accurate to maximize your score and climb the leaderboards!
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.1 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 2.5 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        📊
                                    </motion.span>
                                    <div className="text-left">
                                        <strong className="text-black dark:text-white">Game Report:</strong> After each game, a detailed report is generated showing your answers, correctness, response times, points earned/lost, and final ranking to help you review and improve.
                                    </div>
                                </motion.li>
                            </ul>
                        </Card>
                    </motion.div>
                </div>

                {/* Back Link */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    className="text-center mt-8"
                >
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Link
                            to="/options"
                            className="inline-block font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-zonaProRegular text-base sm:text-lg md:text-xl transition-colors"
                        >
                            ← Back to Game Modes
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default MultiPlayerPage;
